import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import classNames from "classnames";
import { useScopedColorScheme } from "@/utils";

import { ODSBreadcrumbOverflowItemProps } from "./ODSBreadcrumb.types";
import ODSBreadcrumbMenuItem from "./ODSBreadcrumbMenuItem";
import ODSBreadcrumbSeparatorDot from "./ODSBreadcrumbSeparatorDot";

const ODSBreadcrumbOverflowItem = forwardRef<
  HTMLButtonElement,
  ODSBreadcrumbOverflowItemProps
>(({ className, expanded: expandedProp, items, ...rest }, ref) => {
  const isExpandedControlled = expandedProp !== undefined;
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = (value: boolean) => {
    if (!isExpandedControlled) setExpanded(value);
  };

  useEffect(() => {
    if (isExpandedControlled) setExpanded(Boolean(expandedProp));
  }, [expandedProp, isExpandedControlled]);

  const { refs, floatingStyles, context } = useFloating({
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: -16.5 }),
      flip({ padding: 8 }),
      size({
        apply({ elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            width: `auto`,
          });
        },
      }),
    ],
    open: expanded,
    onOpenChange: toggleExpanded,
    placement: "bottom-start",
  });

  const { isMounted, styles: menuStyles } = useTransitionStyles(context, {
    duration: 250,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  });

  useImperativeHandle(ref, () => refs.reference.current! as HTMLButtonElement, [
    refs,
  ]);

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav]
  );

  const { colorScheme, colorMode } = useScopedColorScheme(refs.reference, [
    expanded,
  ]);

  return (
    <>
      <button
        ref={refs.setReference}
        className={classNames(
          `ODSBreadcrumbOverflowItem`,
          expanded && `ODSBreadcrumbOverflowItem--expanded`,
          className
        )}
        {...getReferenceProps()}
        onClick={() => toggleExpanded(!expanded)}
        type="button"
        {...rest}
      >
        <ODSBreadcrumbSeparatorDot />
        <ODSBreadcrumbSeparatorDot />
        <ODSBreadcrumbSeparatorDot />
      </button>
      {isMounted ? (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            initialFocus={0}
            modal={false}
          >
            <div
              className="ODSBreadcrumbMenuListContainer"
              ref={refs.setFloating}
              role="listbox"
              style={{ ...floatingStyles, ...menuStyles }}
              data-scheme={colorScheme}
              data-mode={colorMode}
              {...getFloatingProps()}
            >
              <ol className="ODSBreadcrumbMenuListContainer-List">
                {items.map((itemProps, index) => {
                  const { text, ...rest } = itemProps;
                  return (
                    <li
                      className="ODSBreadcrumbMenuListContainer-List-Item"
                      key={`ODSBreadcrumb-Menu-Item-Container_${index}`}
                    >
                      <ODSBreadcrumbMenuItem
                        {...itemProps}
                        text={text}
                        tabIndex={activeIndex === index ? 0 : -1}
                        ref={(node) => {
                          listRef.current[index] = node;
                        }}
                        role="menuitem"
                        {...getItemProps()}
                        {...rest}
                      />
                    </li>
                  );
                })}
              </ol>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
});

ODSBreadcrumbOverflowItem.displayName = "ODSBreadcrumbOverflowItem";

export default ODSBreadcrumbOverflowItem;
